import { Alert } from "@/deps/ui-library";
import { useFiscalYears, useTrialBalanceAsOn } from "@/api/queries";
import { formatDate, today } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { balanceSheetOf } from "./balanceSheet";
import {
  FrameworkStatementBody,
  StatementFormatSwitch,
  statementExport,
  useFrameworkStatement,
  useStatementFormat,
} from "./FrameworkStatement";
import { DateField, StatementTable, useDateParam } from "./reportParts";
import { ReportFrame } from "./ReportFrame";

/** Assets, liabilities and equity at a date, against the end of the year before when there is one. */
export function BalanceSheetPage() {
  const [asOn, setAsOn] = useDateParam("/reports/balance-sheet", "asOn", today());
  const { data: years } = useFiscalYears();
  const year = years?.find((y) => y.start <= asOn && asOn <= y.end);
  const prior = [...(years ?? [])]
    .sort((a, b) => b.end.localeCompare(a.end))
    .find((y) => year !== undefined && y.end < year.start);

  const { data: current, isPending, error } = useTrialBalanceAsOn(asOn);
  const { data: before } = useTrialBalanceAsOn(prior?.end ?? "", prior !== undefined);

  const sheet = current ? balanceSheetOf(current, prior ? before : undefined) : undefined;
  const period = `As on ${formatDate(asOn)}`;
  const heads = ["", formatDate(asOn), ...(prior ? [formatDate(prior.end)] : [])];
  const chosen = useStatementFormat();
  const framework = useFrameworkStatement(chosen.format, "bs", {
    from: null,
    to: asOn,
    compareFrom: null,
    compareTo: prior?.end ?? null,
  });

  return (
    <ReportFrame
      id="bs"
      title="Balance sheet"
      period={period}
      exportable={
        framework.on
          ? framework.rendered.data
            ? () => statementExport(framework.rendered.data!, period)
            : undefined
          : sheet
            ? () => ({
                title: "Balance sheet",
                period,
                columns: [
                  { header: "Account" },
                  ...heads.slice(1).map((header) => ({ header, numeric: true })),
                ],
                rows: sheet.rows.map((row) => [row.label, ...row.values]),
              })
            : undefined
      }
    >
      <div className="flex flex-wrap items-end gap-2">
        <DateField label="As on" value={asOn} onChange={setAsOn} />
        <StatementFormatSwitch {...chosen} />
      </div>

      {framework.on ? <FrameworkStatementBody framework={framework} /> : null}

      {!framework.on && error ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={error instanceof Error ? error.message : "That report could not be loaded."}
        />
      ) : null}

      {!framework.on && sheet && sheet.difference !== 0 ? (
        <Alert
          role="alert"
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={`Assets differ from liabilities and equity by ${formatAmount((sheet.difference / 100).toFixed(2))}.`}
        />
      ) : null}

      {framework.on ? null : (
        <StatementTable
          caption="Balance sheet"
          columns={heads}
          rows={sheet?.rows ?? []}
          loading={isPending}
        />
      )}
    </ReportFrame>
  );
}
